/**
 * 共享 storage 读写工具(hotel-hub + SDK 共用)
 *
 * 这里集中托管三块同源 storage 的读写契约,消除 hotel-hub 内部与未来 SDK 之间的格式漂移:
 * 1. user-storage:zustand persist 落盘的登录态({ state, version } 结构)
 * 2. hotel-cache:sessionStorage 里的酒店信息缓存(带 TTL + 域名隔离)
 * 3. 日期缓存:localStorage 里用户选择的入住/离店日期
 *
 * 注意:user-storage 只读不写——hotel-hub 侧由 zustand persist 写入,SDK 仅读取;
 * 其余两块双向读写。
 */
import type { User } from '@/types/auth';
import { readHotelCache, writeHotelCache, clearHotelCache, getLastDomain } from '@/lib/hotel-cache';

// ===== user-storage(zustand persist 格式) =====

export const USER_STORAGE_KEY = 'user-storage';

/** zustand persist 落盘结构:{ state, version } */
interface UserStoragePayload {
  state?: {
    user?: User | null;
    token?: string | null;
    isLoggedIn?: boolean;
  };
  version?: number;
}

/**
 * 读取 user-storage 中 persist 的 state。
 * 结构由 zustand persist 决定(token 等字段在 state 下),不直接读裸 key。
 */
export function readUserStorage(): UserStoragePayload['state'] | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(USER_STORAGE_KEY);
    if (!raw) return null;
    return (JSON.parse(raw) as UserStoragePayload)?.state ?? null;
  } catch {
    return null;
  }
}

/** 读取登录 token(供请求层注入 Authorization,hotel-hub 与 SDK 共用同一份) */
export function readUserToken(): string | null {
  return readUserStorage()?.token ?? null;
}

/** 读取登录态(供 SDK 判断是否已登录) */
export function readIsLoggedIn(): boolean {
  return !!readUserStorage()?.isLoggedIn;
}

/**
 * 写入 user-storage(SDK 登录成功后调用)。
 * 匹配 zustand persist 落盘结构 { state, version },hotel-hub user-store 的 storage 事件
 * 监听据此 syncFromStorage 同步内存态 + 关闭登录弹窗(跨 tab / SDK 登录闭环)。
 * 仅写登录态本体(user/token/isLoggedIn),与 user-store partialize 一致。
 */
export function writeUserStorage(state: {
  user: User | null;
  token: string | null;
  isLoggedIn: boolean;
}): void {
  if (typeof window === 'undefined') return;
  try {
    const payload: UserStoragePayload = { state, version: 0 };
    localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // localStorage 不可用时静默忽略
  }
}

// ===== hotel-cache =====
// 实现见 @/lib/hotel-cache,这里透传出统一入口供 SDK 引用
export { readHotelCache, writeHotelCache, clearHotelCache, getLastDomain };

// ===== 日期缓存 =====

export const DATE_CACHE_KEY = 'hotel-hub:selected-dates';

export interface CachedDates {
  checkIn: Date;
  checkOut: Date;
}

/**
 * 从 localStorage 读取缓存的入住/离店日期。
 * 如果缓存不存在或入住日期已早于今天,返回 null。
 */
export function readCachedDates(): CachedDates | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(DATE_CACHE_KEY);
    if (!raw) return null;
    const { checkIn, checkOut } = JSON.parse(raw) as { checkIn: string; checkOut: string };
    const checkInDate = new Date(checkIn);
    const checkOutDate = new Date(checkOut);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    // 缓存日期小于今天则视为过期
    if (checkInDate < today) return null;
    return { checkIn: checkInDate, checkOut: checkOutDate };
  } catch {
    return null;
  }
}

/**
 * 格式化为本地日期字符串 (yyyy-MM-dd 00:00:00)。
 *
 * 内联而非从 @/lib/utils 引入:shared-storage 供 SDK 独立 bundle 复用,
 * 若引入 utils 会经 date-fns/locale barrel 把全部 locale 拉进 SDK bundle。
 * 逻辑与 utils.toLocalDateString 完全一致(日期格式为 readCachedDates 解析契约,稳定)。
 */
function toLocalDateString(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d} 00:00:00`;
}

/**
 * 将用户选择的入住/离店日期写入 localStorage。
 */
export function writeCachedDates(checkIn: Date, checkOut: Date): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(
      DATE_CACHE_KEY,
      JSON.stringify({
        checkIn: toLocalDateString(checkIn),
        checkOut: toLocalDateString(checkOut),
      })
    );
  } catch {
    // localStorage 不可用时静默忽略
  }
}

/**
 * 获取默认入住/离店日期:明天和后天。
 */
export function getDefaultDates(): { checkIn: Date; checkOut: Date } {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  tomorrow.setHours(0, 0, 0, 0);
  const dayAfterTomorrow = new Date(tomorrow);
  dayAfterTomorrow.setDate(dayAfterTomorrow.getDate() + 1);
  return { checkIn: tomorrow, checkOut: dayAfterTomorrow };
}
